import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSnackbar, SnackbarOrigin } from 'notistack';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { notificationApi } from '../services/notificationApi';
import { NotificationType, INotification } from '../types/notification';
import { getStoredToken } from '../utils/auth';

// For notistack compatibility
type NotistackVariant = 'default' | 'error' | 'success' | 'warning' | 'info';

// Map our NotificationType to notistack variants
const mapNotificationTypeToVariant = (type: NotificationType): NotistackVariant => {
  switch (type) {
    case NotificationType.ERROR:
      return 'error';
    case NotificationType.SUCCESS:
      return 'success';
    case NotificationType.WARNING:
      return 'warning';
    case NotificationType.INFO:
      return 'info';
    default:
      return 'default';
  }
};

// Define base notification context value interface
export interface NotificationContextValue {
  success: (message: string) => void;
  notifyError: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
  error: string | null; // Added for compatibility
}

// Define extended interface with additional methods
interface ExtendedNotificationContextValue extends NotificationContextValue {
  notifications: INotification[];
  unreadCount: number;
  isLoading: boolean;
  errorState: Error | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotification: (id: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refetch: () => Promise<void>;
  isWebSocketConnected: boolean;
  setNotifications: React.Dispatch<React.SetStateAction<INotification[]>>;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
}

const defaultSnackbarOptions = {
  autoHideDuration: 3000,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right'
  } as SnackbarOrigin
};

const NotificationContext = createContext<ExtendedNotificationContextValue | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [errorState, setErrorState] = useState<Error | null>(null);
  const [error, setError] = useState<string | null>(null); // Added for compatibility
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setErrorState(null);
      setError(null);
      const response = await notificationApi.getNotifications();
      
      // The notificationApi.getNotifications() returns the data property which contains
      // { notifications, unreadCount, pagination }
      setNotifications(response.notifications || []);
      
      // Use the unreadCount from the API response if available, otherwise calculate it
      if ('unreadCount' in response && typeof response.unreadCount === 'number') {
        setUnreadCount(response.unreadCount);
      } else {
        // Fallback: calculate unread count from the notifications
        const unreadNotifications = (response.notifications || []).filter(
          (notification: INotification) => !notification.read
        );
        setUnreadCount(unreadNotifications.length);
      }
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setErrorState(error as Error);
      setError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Initialize notifications
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Notification display methods
  const showNotification = useCallback((message: string, type: NotificationType) => {
    enqueueSnackbar(message, {
      ...defaultSnackbarOptions,
      variant: mapNotificationTypeToVariant(type)
    });
  }, [enqueueSnackbar]);

  const success = useCallback((message: string) => {
    showNotification(message, NotificationType.SUCCESS);
  }, [showNotification]);

  const notifyError = useCallback((message: string) => {
    showNotification(message, NotificationType.ERROR);
  }, [showNotification]);

  const warning = useCallback((message: string) => {
    showNotification(message, NotificationType.WARNING);
  }, [showNotification]);

  const info = useCallback((message: string) => {
    showNotification(message, NotificationType.INFO);
  }, [showNotification]);

  // Socket.IO connection - only connect when user is authenticated
  useEffect(() => {
    // Only establish connection if user is authenticated
    if (!user) {
      setIsWebSocketConnected(false);
      return;
    }

    const serverUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_WS_URL || 'http://localhost:5000';
    
    // Get auth token using the proper auth utility function
    const token = getStoredToken();
    
    // Validate token format before using it
    const isValidToken = token && 
      typeof token === 'string' && 
      token !== 'undefined' && 
      token !== 'null' && 
      token.split('.').length === 3;
    
    if (!isValidToken) {
      setIsWebSocketConnected(false);
      return;
    }
    

    let socket: Socket;
    
    try {
      // Initialize Socket.IO connection with authentication
      const socketOptions: any = {
        transports: ['websocket', 'polling'],
        auth: { token }
      };
      
      socket = io(serverUrl, socketOptions);

      socket.on('connect', () => {
        setIsWebSocketConnected(true);
      });

      socket.on('disconnect', (reason) => {
        setIsWebSocketConnected(false);
      });

      socket.on('connect_error', (error) => {
        console.error('Socket.IO connection error:', error);
        setIsWebSocketConnected(false);
        
        // Show helpful error message
      });

      socket.on('connection_success', (data) => {
      });

      // Listen for real-time notifications
      socket.on('notification', (data) => {
        try {
          setNotifications(prev => [data, ...prev]);
          setUnreadCount(prev => prev + 1);
          showNotification(data.message, data.type);
        } catch (error) {
          console.error('Error processing notification:', error);
        }
      });

    } catch (error) {
      console.error('Failed to create Socket.IO connection:', error);
      setIsWebSocketConnected(false);
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [user, showNotification]);
  
  // Clear notifications when user logs out
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setIsWebSocketConnected(false);
    }
  }, [user]);

  // Notification management methods
  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, read: true } : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }, []);

  const clearNotification = useCallback(async (id: string) => {
    try {
      await notificationApi.deleteNotification(id);
      setNotifications(prev =>
        prev.filter(notification => notification.id !== id)
      );
    } catch (error) {
      console.error('Error clearing notification:', error);
      throw error;
    }
  }, []);

  const clearAllNotifications = useCallback(async () => {
    try {
      await notificationApi.deleteAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  }, []);

  const value: ExtendedNotificationContextValue = {
    notifications,
    unreadCount,
    isLoading,
    errorState,
    error,
    success,
    notifyError,
    warning,
    info,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
    refetch: fetchNotifications,
    isWebSocketConnected,
    setNotifications,
    setUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
